/**
 * Sidebar — primary navigation for the antifraud operator console.
 *
 * Navigation data (SECTIONS, NavItem, NavEntry, NavSection types) live in
 * ./sidebar-sections.ts to satisfy the react-refresh/only-export-components
 * lint rule. This file exports only the Sidebar component.
 *
 * See sidebar-sections.ts for the full information-architecture comment block.
 */
import { NavLink, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { X, Eye } from 'lucide-react'
import { cn } from '@/lib/cn'
import { findActiveModule, firstVisibleItemPath, navigationModules } from '@/lib/navigation'
import { canSeeNavItem, makeAuthContext } from '@/lib/permissions'
import { useMe } from '@/features/auth/hooks'
import { useQueueDepths } from '@/features/alerts/hooks/useQueueDepths'
import { usePendingBlocks } from '@/features/blocks/hooks/useBlocks'
import { usePlatformTenantOptional } from '@/features/platform/context'
import { SECTIONS } from './sidebar-sections'
import type { NavItem, NavEntry } from './sidebar-sections'

/**
 * Build a lookup: section label → NavigationModule (for O(1) access).
 *
 * Uses `module.sectionLabel` when set (the SECTIONS entry label may differ
 * from the module display label — see the 'tenant-admin' module comment in
 * navigation.ts for the collision-resolution rationale). Falls back to
 * `module.label` for all modules where the two coincide (which is the common case).
 */
const moduleByLabel = new Map(navigationModules.map((m) => [m.sectionLabel ?? m.label, m]))

interface Props {
  /** True on mobile when the drawer is open. Always true on desktop. */
  open: boolean
  /** Close the mobile drawer. Called when nav items are clicked + on
   *  the close button + on backdrop click. */
  onClose: () => void
}

export function Sidebar({ open, onClose }: Props) {
  const { data: me } = useMe()
  const { data: depths } = useQueueDepths()
  const { pathname } = useLocation()
  const canApprove = me?.permissions.includes('blocks:approve') ?? false
  const { data: pendingBlocks } = usePendingBlocks()
  const pendingCount = canApprove ? (pendingBlocks?.total ?? 0) : 0

  // Phase 3 (tenant-view switcher): read the selected tenant so we can pass
  // tenantViewId to makeAuthContext. usePlatformTenantOptional returns null
  // when the provider is not in scope (shouldn't happen post-promotion, but
  // safe for standalone test renders). For tenant-scoped users the provider
  // is present but selectedTenantId is always null (they never select a tenant).
  const platformTenant = usePlatformTenantOptional()
  const tenantViewId = platformTenant?.selectedTenantId ?? null

  // Build the central AuthContext once; all predicates use it.
  // tenantViewId drives tenant-view-mode nav in canSeeNavItem (permissions.ts).
  const ctx = makeAuthContext(me, tenantViewId)

  // H4 fix (ADR-0024, Phase 4): platform admins in PLATFORM mode must NOT see
  // Fraud Queues — queue depths are tenant-operational data and calling
  // useQueueDepths() as a platform admin (no tenant context) produces either
  // meaningless cross-tenant aggregates or a backend 403. The section is only
  // meaningful for:
  //   (a) tenant-scoped users (is_platform_admin:false), or
  //   (b) platform admins who have selected a tenant (tenant-view mode).
  // A platform admin in platform mode (selectedTenantId === null) MUST NOT see
  // this section. The boolean below gates the render — computed from the same
  // context that drives canSeeNavItem so the logic is consistent.
  // S1 simplification: !isPlatformAdmin covers tenant users; tenantViewId !== null
  // covers the tenant-view-mode platform-admin case. The redundant middle term dropped.
  const showFraudQueues = !ctx.isPlatformAdmin || tenantViewId !== null

  // Lock the page from scrolling behind the open drawer on mobile.
  useEffect(() => {
    if (!open) return
    const w = typeof window !== 'undefined' ? window.matchMedia('(max-width: 1023px)').matches : false
    if (!w) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // Central permission filter using canSeeNavItem from permissions.ts.
  // Semantics:
  //   * scope:'platform' → requires ctx.isPlatformAdmin (no system:admin bypass)
  //   * scope:'tenant'/'both' with no permissions → visible to all authenticated users
  //   * system:admin → short-circuits all non-platform checks
  //   * otherwise → show if user holds ANY of the listed permissions
  //
  // Subheaders are kept only when they have at least one visible item beneath
  // them (before the next subheader or end of section); orphan subheaders are
  // stripped so the rendered nav never shows an empty group title.
  //
  // Fail-closed: items with permission requirements stay hidden until /me
  // resolves (ctx.permissions is [] when me is undefined).
  const visibleSections = SECTIONS.map((section) => {
    const itemVisible = (it: NavItem): boolean => {
      return canSeeNavItem(
        { permissions: it.permissions, scope: it.scope, platformAdminOnly: it.platformAdminOnly },
        ctx,
      )
    }

    // First pass: filter items but keep subheaders.
    const passOne: NavEntry[] = section.items.filter((entry) =>
      entry.kind === 'subheader' ? true : itemVisible(entry),
    )
    // Second pass: drop subheaders that have no visible items beneath.
    const passTwo: NavEntry[] = []
    for (let i = 0; i < passOne.length; i++) {
      const entry = passOne[i]
      if (!entry) continue
      if (entry.kind === 'subheader') {
        // Look ahead — keep this subheader only if there's at least
        // one non-subheader entry before the next subheader.
        let hasItem = false
        for (let j = i + 1; j < passOne.length; j++) {
          const next = passOne[j]
          if (!next || next.kind === 'subheader') break
          hasItem = true
          break
        }
        if (!hasItem) continue
      }
      passTwo.push(entry)
    }
    return { ...section, items: passTwo }
  }).filter((s) => s.items.some((e) => e.kind !== 'subheader'))

  return (
    <>
      {/* Backdrop on mobile when the drawer is open */}
      <button
        type="button"
        aria-hidden={!open}
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-30 bg-black/40 transition-opacity lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <nav
        aria-label="Primary"
        className={cn(
          'flex w-64 flex-col gap-0.5 overflow-y-auto border-r border-border bg-card p-2 pb-4',
          // Desktop: relative column inside the AppShell grid
          'lg:relative lg:translate-x-0 lg:shadow-none',
          // Mobile: fixed off-canvas drawer that slides in
          'fixed inset-y-0 left-0 z-40 transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Mobile-only close button */}
        <div className="flex items-center justify-between px-2 pt-1 pb-2 lg:hidden">
          <span className="text-sm font-semibold">Menu</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
          >
            <X size={16} />
          </button>
        </div>

        {visibleSections.map((section) => {
          // Check whether this section has a registered navigation module.
          // If yes → render a single consolidated NavLink (icon from module config).
          // If no  → render the section's flat item(s) directly (Platform, Settings).
          const registeredModule = section.label ? moduleByLabel.get(section.label) : undefined

          // For a consolidated entry: only render if the user can see at least
          // one module item. `visibleSections` already ensures section.items has
          // at least one non-subheader; cross-check against module items using
          // canSeeNavItem so we don't show a dead consolidated link.
          const moduleVisible = registeredModule
            ? registeredModule.items.some((item) => canSeeNavItem(item, ctx))
            : false

          const moduleActive =
            registeredModule !== undefined &&
            findActiveModule(pathname)?.key === registeredModule.key

          // Fix W3 (maintenance safety): DEV-only guard. If a section label maps to
          // a registered NavigationModule but the module is not visible (moduleVisible
          // === false while registeredModule is defined), the SECTIONS and
          // navigationModules arrays have drifted. Log clearly in dev.
          if (import.meta.env.DEV && registeredModule && !moduleVisible) {
            console.error(
              '[Sidebar] SECTIONS/navigationModules out of sync for module:',
              registeredModule.key,
              '— the section has items but no navigationModules entry item is visible to this user.',
            )
          }

          // The Settings section (null label) gets a thin divider above it for
          // visual separation at the bottom — no text header.
          const isSettingsSection = section.label === null

          if (registeredModule && moduleVisible) {
            const { Icon, label } = registeredModule
            // Navigate to the first page the user can actually access.
            const landingPath = firstVisibleItemPath(registeredModule, ctx.permissions, ctx.isPlatformAdmin)
            return (
              <NavLink
                key={`${registeredModule.key}-consolidated`}
                to={landingPath}
                onClick={onClose}
                // Fix W2 a11y: NavLink's internal aria-current is driven by its own
                // path match against `to` (the landing page). On a sub-page like
                // /cases, the Investigations link is visually active via moduleActive
                // but NavLink would emit no aria-current because the current URL is
                // not exactly the landing path. Set aria-current explicitly from
                // moduleActive so screen-reader users can always identify the active
                // primary section. The explicit prop overrides NavLink's internal one.
                aria-current={moduleActive ? 'page' : undefined}
                // Fix W1 (onboarding tour): expose stable anchor IDs on consolidated
                // module links so OnboardingTour.tsx can look them up via data-tour-id.
                // Each module key maps 1:1 to the consolidated link rendered here.
                data-tour-id={`${registeredModule.key}-consolidated-link`}
                className={cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                  moduleActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-fg hover:bg-muted',
                )}
              >
                <Icon size={15} className="shrink-0" />
                <span className="flex-1 truncate">{label}</span>
              </NavLink>
            )
          }

          // Non-module section (Platform, Settings): render flat items directly.
          // No section-header text — each item is a top-level link in the flat list.
          // Note: the 'Tenant Admin' section is now a registered module ('tenant-admin')
          // so it is handled by the consolidated path above; it never reaches this branch.
          // Subheader entries (kind:'subheader') do not appear in any current flat section
          // (Platform has one item; Settings has one item). The renderer is intentionally
          // simple — no dead code for subheader rendering.
          return (
            <div
              key={section.label ?? 'settings-section'}
              className={cn(
                'flex flex-col gap-0.5',
                isSettingsSection ? 'mt-2 border-t border-border/60 pt-2' : '',
              )}
            >
              {section.items.map((entry) => {
                if (entry.kind === 'subheader') {
                  // Flat sections (Platform, Settings) have no subheaders; this branch
                  // is kept only as a defensive guard so any future data-only addition
                  // of a subheader to a flat section does not crash the renderer.
                  return null
                }
                const { to, label, Icon } = entry
                return (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-fg hover:bg-muted',
                      )
                    }
                  >
                    <Icon size={15} className="shrink-0" />
                    <span className="flex-1 truncate">{label}</span>
                  </NavLink>
                )
              })}
            </div>
          )
        })}

        {/* Block approvals — conditional link for users with blocks:approve.
            Rendered outside the SECTIONS loop so it is never swallowed by
            the consolidated Compliance entry. Compliance is now a single link
            to /compliance; this approval queue link stays reachable here. */}
        {canApprove && (
          <NavLink
            to="/blocks/approvals"
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                'mt-3 flex items-center gap-2 rounded px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-fg hover:bg-muted',
              )
            }
          >
            <Eye size={14} className="opacity-70" />
            <span className="flex-1 text-xs">Block approvals</span>
            {pendingCount > 0 && (
              <span className="rounded-full bg-status-pending/20 px-1.5 py-0.5 font-mono text-[0.625rem] tabular-nums text-status-pending">
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </NavLink>
        )}

        {showFraudQueues && depths && depths.queues.length > 0 && (
          <div className="mt-3 flex flex-col gap-0.5">
            <p className="px-3 py-1 text-[0.625rem] font-semibold uppercase tracking-wide text-muted-fg">
              Fraud queues
            </p>
            {depths.queues.map((q) => {
              const total = q.open_count + q.in_review_count
              return (
                <NavLink
                  key={q.queue_id}
                  to={`/alerts?queue=${q.queue_id}`}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-2 rounded px-3 py-1.5 text-sm transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-fg hover:bg-muted',
                    )
                  }
                >
                  <span
                    aria-hidden
                    className={cn(
                      'inline-block h-1.5 w-1.5 rounded-full',
                      q.sla_breached_count > 0
                        ? 'bg-status-failed'
                        : total > 0
                          ? 'bg-status-info'
                          : 'bg-muted-fg/40',
                    )}
                  />
                  <span className="flex-1 truncate text-xs">{q.queue_name}</span>
                  <span className="font-mono text-[0.625rem] tabular-nums text-muted-fg">
                    {total}
                  </span>
                  {q.sla_breached_count > 0 && (
                    <span
                      className="rounded-sm bg-status-failed/15 px-1 font-mono text-[0.625rem] text-status-failed"
                      title={`${q.sla_breached_count} SLA-breached`}
                    >
                      !{q.sla_breached_count}
                    </span>
                  )}
                </NavLink>
              )
            })}
          </div>
        )}
      </nav>
    </>
  )
}
